import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Container,
  Card,
  CardContent,
  Grid,
  Avatar,
  useTheme,
  alpha,
  Fade,
  Slide,
  Skeleton,
  LinearProgress,
  Chip,
  Stack,
  Tooltip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  CardMedia,
  Checkbox,
  Switch
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Inventory,
  Add,
  Refresh,
  Download,
  Search,
  FilterList,
  Clear,
  Image,
  ShoppingCart,
  Visibility,
  CloudUpload
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack';
import { getProducts, createProduct, updateProduct, deleteProduct, updateOrder } from '../../services/productService';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [photoFilter, setPhotoFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    status: 'active',
    showPhoto: true,
    photo: ''
  });
  
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts();
      const items = Array.isArray(data) ? data : (data?.data || []);
      setProducts(items);
      applyFilters(items, searchQuery, statusFilter, photoFilter);
      enqueueSnackbar('Товары загружены', { variant: 'success' });
    } catch (error) {
      console.error('Error loading products:', error);
      enqueueSnackbar('Ошибка загрузки товаров', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Search and filtering
  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    applyFilters(products, query, statusFilter, photoFilter);
  };

  const handleStatusFilter = (event) => {
    const status = event.target.value;
    setStatusFilter(status);
    applyFilters(products, searchQuery, status, photoFilter);
  };

  const handlePhotoFilter = (event) => {
    const photo = event.target.value;
    setPhotoFilter(photo);
    applyFilters(products, searchQuery, statusFilter, photo);
  };

  const applyFilters = (productsData, query, status, photo) => {
    let filtered = productsData.filter(product => 
      product.name?.toLowerCase().includes(query)
    );

    if (status === 'active') {
      filtered = filtered.filter(product => product.status === 'active');
    } else if (status === 'inactive') {
      filtered = filtered.filter(product => product.status === 'inactive');
    }

    if (photo === 'with_photo') {
      filtered = filtered.filter(product => product.photo && product.photo.trim() !== '');
    } else if (photo === 'without_photo') {
      filtered = filtered.filter(product => !product.photo || product.photo.trim() === '');
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPhotoFilter('all');
    applyFilters(products, '', 'all', 'all');
  };

  // Export functionality
  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();
      
      const exportData = filteredProducts.map((product, index) => ({
        '№': index + 1,
        'Название': product.name,
        'Статус': product.status === 'active' ? 'Активный' : 'Неактивный',
        'Показывать фото': product.showPhoto ? 'Да' : 'Нет',
        'Есть фото': product.photo && product.photo.trim() !== '' ? 'Да' : 'Нет',
        'Дата создания': new Date(product.createdAt || Date.now()).toLocaleString('ru-RU'),
        'Порядок': product.order || index
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      const colWidths = [
        { wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 10 }
      ];
      worksheet['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Товары');
      
      const fileName = `products_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      enqueueSnackbar('Данные экспортированы в Excel', { variant: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Ошибка экспорта данных', { variant: 'error' });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(products);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setProducts(items);
    try {
      const orderedProducts = items.map((product, index) => ({
        id: product._id,
        order: index
      }));
      await updateOrder(orderedProducts);
    } catch (error) {
      console.error('Error updating order:', error);
      loadProducts();
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      status: 'active',
      showPhoto: true,
      showDescription: true,
      photo: '',
      description: '',
      discount: 'без скидки'
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      photo: '',
      showPhoto: false,
      showDescription: false
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      photo: product.photo || '',
      showPhoto: product.showPhoto,
      showDescription: product.showDescription
    });
    setOpenDialog(true);
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await deleteProduct(productId);
      await loadProducts();
      enqueueSnackbar('Товар удален', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting product:', error);
      enqueueSnackbar('Не удалось удалить товар', { variant: 'error' });
    }
  };

  const handleToggleStatus = async (productId) => {
    const product = products.find(p => p._id === productId);
    if (!product) {
      console.error('Product not found');
      return;
    }
    const newStatus = product.status === 'active' ? 'inactive' : 'active';

    try {
      const updatedProduct = await updateProduct(productId, { status: newStatus });
      setProducts(products.map(product =>
        product._id === productId
          ? updatedProduct
          : product
      ));
    } catch (error) {
      console.error('Error updating product status:', error);
    }
  };

  const handleSaveProduct = async () => {
    try {
      const productData = { ...formData };
      if (editingProduct) {
        await updateProduct(editingProduct._id, productData);
        enqueueSnackbar('Товар успешно обновлен', { variant: 'success' });
      } else {
        await createProduct(productData);
        enqueueSnackbar('Товар успешно добавлен', { variant: 'success' });
      }
      await loadProducts();
      handleClose();
    } catch (error) {
      console.error('Error saving product:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Ошибка при сохранении товара';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Hero Section */}
        <Paper 
          elevation={4}
          sx={{ 
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <Inventory sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Управление товарами
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Настройка товаров, изображений и порядка отображения
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Экспорт в Excel">
                <IconButton 
                  onClick={exportToExcel}
                  disabled={filteredProducts.length === 0 || loading}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title="Обновить данные">
                <IconButton 
                  onClick={loadProducts}
                  disabled={loading}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  {loading ? <LinearProgress size={20} sx={{ color: 'text.primary' }} /> : <Refresh />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        {/* Search and Filters */}
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Search color="primary" />
                Поиск и фильтры
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  onClick={clearFilters}
                  startIcon={<Clear />}
                  disabled={loading}
                >
                  Сбросить
                </Button>
                <Button 
                  variant="contained"
                  onClick={handleAddProduct}
                  startIcon={<Add />}
                  disabled={loading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Добавить товар
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Поиск товаров"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Введите название товара"
                  disabled={loading}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3.5}>
                <FormControl fullWidth>
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilter}
                    label="Статус"
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      }
                    }}
                  >
                    <MenuItem value="all">Все статусы</MenuItem>
                    <MenuItem value="active">Только активные</MenuItem>
                    <MenuItem value="inactive">Только неактивные</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3.5}>
                <FormControl fullWidth>
                  <InputLabel>Фото</InputLabel>
                  <Select
                    value={photoFilter}
                    onChange={handlePhotoFilter}
                    label="Фото"
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      }
                    }}
                  >
                    <MenuItem value="all">Все товары</MenuItem>
                    <MenuItem value="with_photo">С фото</MenuItem>
                    <MenuItem value="without_photo">Без фото</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width={100} height={24} />
                  </Box>
                  <Skeleton variant="text" width={80} height={48} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={150} height={20} />
                </Card>
              </Grid>
            ))
          ) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <ShoppingCart />
                    </Avatar>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      Всего товаров
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {products.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    отфильтровано: {filteredProducts.length}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.15)}`
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      Активные
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {products.filter(p => p.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    товаров активны
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.warning.main, 0.15)}`
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 2 }}>
                      <Image />
                    </Avatar>
                    <Typography variant="h6" color="warning.main" fontWeight="bold">
                      С фото
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="warning.main">
                    {products.filter(p => p.photo && p.photo.trim() !== '').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    товаров с изображением
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.info.main, 0.15)}`
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.info.main, mr: 2 }}>
                      <Visibility />
                    </Avatar>
                    <Typography variant="h6" color="info.main" fontWeight="bold">
                      Показывать фото
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="info.main">
                    {products.filter(p => p.showPhoto).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    товаров показывают фото
                  </Typography>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {/* Products Table */}
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">
              Список товаров ({loading ? '...' : filteredProducts.length})
            </Typography>
            {!loading && filteredProducts.length === 0 && products.length > 0 && (
              <Chip 
                icon={<FilterList />} 
                label="Нет результатов" 
                color="warning" 
                variant="outlined" 
              />
            )}
          </Box>
          {loading && <LinearProgress />}
          
          {loading ? (
            <Box sx={{ p: 3 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2 }}>
                  <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="30%" height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="rectangular" width={60} height={40} sx={{ mr: 2, borderRadius: 1 }} />
                  <Skeleton variant="text" width="20%" height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Inventory sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {products.length === 0 ? 'Нет товаров' : 'Нет результатов'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {products.length === 0 
                  ? 'Добавьте первый товар для начала работы'
                  : 'Попробуйте изменить параметры поиска'
                }
              </Typography>
              {products.length === 0 && (
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={handleAddProduct}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Добавить первый товар
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell width={50} sx={{ fontWeight: 'bold' }}>
                        <DragIndicatorIcon color="action" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Название</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 'bold' }}>Фото</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 'bold' }}>Параметры</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                      <TableCell width={150} align="right" sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <Droppable droppableId="products">
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {filteredProducts.map((product, index) => (
                          <Draggable key={product._id} draggableId={product._id} index={index}>
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  backgroundColor: snapshot.isDragging ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                    transform: 'translateX(4px)'
                                  }
                                }}
                              >
                                <TableCell {...provided.dragHandleProps}>
                                  <DragIndicatorIcon 
                                    sx={{ 
                                      color: snapshot.isDragging ? theme.palette.primary.main : 'action.active',
                                      cursor: 'grab',
                                      '&:active': { cursor: 'grabbing' }
                                    }} 
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1" fontWeight="medium">
                                    {product.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {product.photo ? (
                                    <Box sx={{ position: 'relative' }}>
                                      <CardMedia
                                        component="img"
                                        sx={{ 
                                          width: 60, 
                                          height: 40, 
                                          borderRadius: 1,
                                          objectFit: 'cover',
                                          border: `1px solid ${theme.palette.divider}`
                                        }}
                                        image={product.photo}
                                        alt={product.name}
                                      />
                                      <Chip
                                        icon={<Image />}
                                        label="Есть"
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                        sx={{ 
                                          position: 'absolute',
                                          top: -8,
                                          right: -8,
                                          fontSize: '0.7rem',
                                          height: 16
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Box sx={{ 
                                      width: 60, 
                                      height: 40, 
                                      borderRadius: 1,
                                      border: `2px dashed ${theme.palette.divider}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: alpha(theme.palette.grey[500], 0.1)
                                    }}>
                                      <PhotoCameraIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.5}>
                                    <Chip
                                      icon={<Visibility />}
                                      label={product.showPhoto ? 'Показывать' : 'Скрывать'}
                                      color={product.showPhoto ? 'info' : 'default'}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={product.status === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
                                    label={product.status === 'active' ? 'Активный' : 'Неактивный'}
                                    color={product.status === 'active' ? 'success' : 'error'}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleToggleStatus(product._id)}
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { 
                                        backgroundColor: product.status === 'active' 
                                          ? alpha(theme.palette.success.main, 0.1)
                                          : alpha(theme.palette.error.main, 0.1)
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Tooltip title="Редактировать">
                                      <IconButton
                                        onClick={() => handleEditProduct(product)}
                                        size="small"
                                        sx={{
                                          color: theme.palette.primary.main,
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Удалить">
                                      <IconButton
                                        onClick={() => handleDeleteProduct(product._id)}
                                        size="small"
                                        sx={{
                                          color: theme.palette.error.main,
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>
              </DragDropContext>
            </TableContainer>
          )}
        </Paper>

        {/* Enhanced Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            bgcolor: 'background.paper',
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main' }}>
              <Inventory />
            </Avatar>
            {editingProduct ? 'Редактировать товар' : 'Добавить новый товар'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Название товара"
              fullWidth
              variant="outlined"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Введите название товара"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  }
                }
              }}
            />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                📷 Фото товара
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                label="Ссылка на фото"
                name="photo"
                value={formData.photo}
                onChange={handleInputChange}
                placeholder="https://example.com/photo.jpg"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    }
                  }
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Вставьте прямую ссылку на изображение (URL)
              </Typography>
              
              {formData.photo && (
                <Box sx={{ mt: 2 }}>
                  <Card elevation={2} sx={{ maxWidth: 200, mx: 'auto', borderRadius: 2 }}>
                    <CardMedia
                      component="img"
                      height="120"
                      image={formData.photo}
                      alt="Preview"
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </Card>
                </Box>
              )}
            </Box>

            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showPhoto}
                    onChange={(e) => setFormData({ ...formData, showPhoto: e.target.checked })}
                    color="info"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Visibility color="info" />
                    <Typography variant="body1" fontWeight="medium">
                      Показывать фото в приложении
                    </Typography>
                  </Box>
                }
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                Включите, чтобы фото отображалось в мобильном приложении
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => setOpenDialog(false)}
              sx={{ borderRadius: 2 }}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSaveProduct} 
              variant="contained"
              disabled={!formData.name.trim()}
              sx={{
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': {
                  background: theme.palette.action.disabledBackground
                }
              }}
            >
              {editingProduct ? 'Сохранить изменения' : 'Добавить товар'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );
};

export default Products;
